import React, { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import API_BASE_URL from '../config/apiConfig';
import emptyStateImg from '../assets/empty_house.png';
import houseThumb from '../assets/ngoinha1.png';
import aromaImg from '../assets/aroma.png';
import airImg from '../assets/air.png';
import humiImg from '../assets/humi.png';
import speakerImg from '../assets/speaker.png';
import lampImg from '../assets/smartlamp.png';

const Dashboard = ({ isVisible, onLogout, onSetup, hasSpace = false, connectedDevices = [], onViewSpaceMap, onNavClick, onDeviceClick, onOpenChatBot }) => {
  // Dữ liệu thiết bị - khai báo trước hooks
  const allDevices = [
    { id: 1, name: 'Smart Lamp', room: 'Living Room', icon: '💡', color: '#93c5fd' },
    { id: 2, name: 'Aroma Diffuser', room: 'Living Room', icon: '🌬️', color: '#ffffff' },
    { id: 3, name: 'Speaker', room: 'Living Room', icon: '🔊', color: '#ffffff' },
    { id: 4, name: 'Speaker', room: 'Bedroom', icon: '🔊', color: '#ffffff' },
    { id: 5, name: 'Air Conditioner', room: 'Living Room', icon: '❄️', color: '#ffffff' },
    { id: 6, name: 'Smart Lamp', room: 'Living Room', icon: '💡', color: '#1f2937' },
    { id: 7, name: 'Smart Socket', room: 'Living Room', icon: '🔌', color: '#ffffff' },
  ];

  // Hooks phải được gọi trước bất kỳ early return nào
  const [deviceStates, setDeviceStates] = useState(
    allDevices.reduce((acc, device) => {
      acc[device.id] = device.name === 'Smart Socket' ? false : true; // Smart Socket mặc định tắt
      return acc;
    }, {})
  );
  const [socketPower, setSocketPower] = useState(0.0);
  const [socketLoading, setSocketLoading] = useState(false);

  // Tìm Smart Socket device
  const smartSocketDevice = allDevices.find(d => d.name === 'Smart Socket');

  // Hàm lấy hình ảnh thiết bị - Phải định nghĩa trước early return
  const getDeviceImage = (name) => {
    if (name.includes('Lamp')) return lampImg;
    if (name.includes('Speaker')) return speakerImg;
    if (name.includes('Aroma')) return aromaImg;
    if (name.includes('Humidifier')) return humiImg;
    if (name.includes('Air Conditioner')) return airImg;
    return null;
  };

  // Fetch power và status cho Smart Socket - Phải gọi trước early return
  useEffect(() => {
    if (!isVisible || !smartSocketDevice) return;

    const fetchPower = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/devices/power`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });
        
        if (response.ok) {
          const data = await response.json();
          const powerValue = data.power || 0.0;
          setSocketPower(powerValue);
          console.log('Power updated:', powerValue, 'W');
        } else {
          console.error('Failed to fetch power:', response.status, response.statusText);
        }
      } catch (error) {
        console.error('Error fetching power:', error);
      }
    };

    const fetchStatus = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/devices/status`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });
        
        if (response.ok) {
          const data = await response.json();
          const newStatus = data.isOn || false;
          const socketDeviceId = smartSocketDevice.id;
          // Chỉ cập nhật nếu khác với trạng thái hiện tại và không đang loading (tránh loop)
          if (newStatus !== deviceStates[socketDeviceId] && !socketLoading) {
            console.log('Status synced from server:', newStatus ? 'ON' : 'OFF');
            setDeviceStates(prev => ({
              ...prev,
              [socketDeviceId]: newStatus
            }));
          }
        }
      } catch (error) {
        console.error('Error fetching status:', error);
      }
    };

    // Fetch ngay lần đầu
    fetchPower();
    fetchStatus();

    // Cập nhật mỗi 1 giây
    const powerInterval = setInterval(fetchPower, 1000);
    const statusInterval = setInterval(fetchStatus, 1000); // Polling status mỗi 1 giây

    return () => {
      clearInterval(powerInterval);
      clearInterval(statusInterval);
    };
  }, [isVisible, smartSocketDevice, deviceStates, socketLoading]);

  // Toggle device - với logic đặc biệt cho Smart Socket
  const toggleDevice = async (deviceId) => {
    const device = allDevices.find(d => d.id === deviceId);
    const isSmartSocket = device && device.name === 'Smart Socket';
    
    if (isSmartSocket) {
      // Logic đặc biệt cho Smart Socket - gọi API
      const newValue = !deviceStates[deviceId];
      setSocketLoading(true);
      
      try {
        const cmd = newValue ? "ON" : "OFF";
        console.log('Toggling socket to:', cmd);
        
        const response = await fetch(`${API_BASE_URL}/api/devices/led?state=${cmd}`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const result = await response.text();
          console.log('Toggle response:', result);
          setDeviceStates(prev => ({
            ...prev,
            [deviceId]: newValue
          }));
        } else {
          const errorText = await response.text();
          throw new Error(`Server returned ${response.status}: ${errorText}`);
        }
      } catch (error) {
        console.error('Error toggling socket:', error);
        alert(`Lỗi khi ${newValue ? 'bật' : 'tắt'} socket: ${error.message}`);
        // Không thay đổi state nếu lỗi
      } finally {
        setSocketLoading(false);
      }
    } else {
      // Logic bình thường cho các device khác
      setDeviceStates(prev => ({
        ...prev,
        [deviceId]: !prev[deviceId]
      }));
    }
  };

  const members = [
    { id: 1, name: 'Kristin', avatar: '👩' },
    { id: 2, name: 'Albert', avatar: '👨' },
    { id: 3, name: 'Annette', avatar: '👩' },
  ];

  if (!isVisible) return null;

  // Nếu chưa có space, hiển thị empty state
  if (!hasSpace) {
    return (
      <div className="dashboard-container " style={{display: 'flex',flex:1}}>
        <Sidebar activeId="spaces" onLogout={onLogout} onNavClick={onNavClick} />
        <div className="main-content"style={{display: 'flex',flex:1}}>
          <div className="empty-state">
            <h2>Looks like you have no spaces set up.</h2>
            <p>Add your house and start your smart life!</p>
            <div className="illustration-box">
              <img src={emptyStateImg} alt="No spaces" className="empty-img" />
            </div>
            <button className="primary-btn" onClick={onSetup}>
              Set up your space
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <Sidebar activeId="spaces" onLogout={onLogout} onNavClick={onNavClick} />

      <div className="main-content dashboard-scroll">
        {/* Header Section */}
        <div className="dashboard-header">
          <div className="header-left">
            <h1 className="dashboard-title">Spaces</h1>
          </div>
          <div className="header-right">
            <div className="space-selector">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              </svg>
              <span>My Home</span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </div>
          </div>
        </div>

        {/* Info Cards */}
        <div className="info-cards-grid">
          <div className="info-card weather">
            <div className="info-icon">⛅</div>
            <div className="info-content">
              <div className="info-label">Partly Cloudy</div>
              <div className="info-value">23°</div>
            </div>
          </div>
          <div className="info-card humidity">
            <div className="info-icon">💧</div>
            <div className="info-content">
              <div className="info-label">Humidity</div>
              <div className="info-value">67%</div>
            </div>
          </div>
          <div className="info-card lights">
            <div className="info-icon">💡</div>
            <div className="info-content">
              <div className="info-label">All lights on</div>
              <div className="info-value">Home</div>
            </div>
          </div>
          <div className="info-card music">
            <div className="info-icon">🎵</div>
            <div className="info-content">
              <div className="info-label">Play music</div>
              <div className="info-value">Living room</div>
            </div>
          </div>
        </div>

        {/* Your Devices Section */}
        <div className="devices-section">
          <div className="section-header">
            <h2 className="section-title">Your Devices</h2>
            <span className="section-badge">{allDevices.length}</span>
          </div>
          <div className="device-grid">
            {allDevices.map((device) => (
              <div 
                key={device.id} 
                className="dashboard-device-card"
                onClick={() => onDeviceClick && onDeviceClick(device.name, device.room)}
                style={{ cursor: 'pointer' }}
              >
                <div className="device-icon-wrapper" style={{ backgroundColor: device.color }}>
                  {device.name === 'Smart Socket' ? (
                    <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke={deviceStates[device.id] ? "#ff8c00" : "#9ca3af"} strokeWidth="1.5" style={{padding: '8px', transition: 'stroke 0.3s'}}>
                      <path d="M12 2L2 7l10 5 10-5-10-5z" />
                      <path d="M2 17l10 5 10-5" />
                      <path d="M2 12l10 5 10-5" />
                    </svg>
                  ) : getDeviceImage(device.name) ? (
                    <img src={getDeviceImage(device.name)} alt={device.name} style={{width: '100%', height: '100%', objectFit: 'contain', padding: '8px'}} />
                  ) : (
                    <span className="device-emoji">{device.icon}</span>
                  )}
                </div>
                <div className="device-info">
                  <h4 className="device-name">{device.name}</h4>
                  <p className="device-room">{device.room}</p>
                  {device.name === 'Smart Socket' && (
                    <p className="device-power" style={{ 
                      fontSize: '0.85rem', 
                      color: socketPower > 0 ? '#10b981' : '#6b7280',
                      marginTop: '4px',
                      fontWeight: '600'
                    }}>
                      {socketPower.toFixed(2)} W
                    </p>
                  )}
                </div>
                <label 
                  className="toggle-switch"
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    checked={deviceStates[device.id]}
                    onChange={() => toggleDevice(device.id)}
                    disabled={device.name === 'Smart Socket' && socketLoading}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom Cards */}
        <div className="bottom-cards-grid">
          <div className="bottom-card members-card">
            <h3 className="bottom-card-title">Members</h3>
            <div className="members-list">
              {members.map((member) => (
                <div key={member.id} className="member-avatar-circle">
                  {member.avatar}
                </div>
              ))}
              <button className="add-member-btn">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
              </button>
            </div>
          </div>

          <div className="bottom-card map-card" onClick={onViewSpaceMap} style={{ cursor: 'pointer' }}>
            <h3 className="bottom-card-title">Your space map</h3>
            <p className="map-description">See your rooms and all the devices that are related to them.</p>
            <div className="map-preview">
              <svg width="100%" height="120" viewBox="0 0 200 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="20" y="20" width="80" height="80" stroke="#e5e7eb" strokeWidth="2" fill="#fef3c7" />
                <rect x="100" y="20" width="80" height="40" stroke="#e5e7eb" strokeWidth="2" fill="white" />
                <rect x="100" y="60" width="80" height="40" stroke="#e5e7eb" strokeWidth="2" fill="white" />
                <line x1="20" y1="60" x2="100" y2="60" stroke="#e5e7eb" strokeWidth="2" />
                <line x1="100" y1="40" x2="180" y2="40" stroke="#e5e7eb" strokeWidth="2" />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
